import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ShortId from 'shortid';
import deepcopy from 'deepcopy';
import GridLoader from '@hobs/components/Grid/GridLoader';
import Columns from '@hobs/components/Grid/Columns';
import { withTranslation } from 'react-i18next';
import { RaisedButton, GhostButton } from '@hobs/components/Buttons';
import Icon from '@hobs/components/Icon';
import Confirm from '@hobs/components/Confirm';
import Toast from '@hobs/components/Toast';
import Switch from '@hobs/components/Switch';
import { H1 } from '@hobs/components/Headings';
import { Grid } from '@progress/kendo-react-grid';
import { createLoadingSelector } from '@hobs/utils/loading';
import {
  withDataStateLocalOverrides,
  withGridLocalOverrides,
  onUserResetSettings
} from '@hobs/utils/grid/overrides';
import { queryOData, resetOData } from '@hobs/actions/odata';
import { PAGE_SIZES } from '@hobs/utils/constants';
import { deleteActivity } from '@hobs/actions/activities';
import ActivityDialog from './ActivityDialog';

import './Activities.scss';

class Activities extends React.PureComponent {
  constructor(props) {
    super(props);
    this.grid = React.createRef();

    this.defaultGridColumns = this.getDefaultColumns();

    this.defaultDataState = {
      take: 5,
      skip: 0,
      sort: [{ field: 'Name', dir: 'asc' }]
    };

    const defaultState = {
      dataState: withDataStateLocalOverrides(
        deepcopy(this.defaultDataState),
        this.LSdataStateName
      ),
      gridKey: ShortId.generate(),
      customFilters: {},
      columns: withGridLocalOverrides(
        deepcopy(this.defaultGridColumns),
        this.LSGridPropsName
      ),
      openDialog: false,
      selectedRows: {},
      isAllSelected: false,
      previousWindowScrollOffset: null
    };

    this.state = {
      ...defaultState
    };
  }


  LSdataStateName = 'ActivitiesSN.dataState-20210217';
  LSGridPropsName = 'ActivitiesSN.grid.props-20210217'; 

  onUserSaveSettings = () => {
    localStorage.setItem(this.LSdataStateName, JSON.stringify(this.state.dataState));
    localStorage.setItem(
      this.LSGridPropsName,
      JSON.stringify({
        columns: this.grid.current.columns
      })
    );
  };

  onUserResetSettings = onUserResetSettings(
    this.LSdataStateName,
    this.LSGridPropsName,
    null,
    () => {
      const columns = this.getDefaultColumns();

      this.setState(
        {
          gridKey: ShortId.generate(),
          columns,
          dataState: this.defaultDataState
        },
        () => {
          this.loadODataQuery();
        }
      );
    }
  );

  componentDidMount() {
    this.loadODataQuery();
  }

  onRemove(activityId) {
    const { t, deleteActivity } = this.props;
    Confirm(
      t('Delete activity'),
      t('Are you sure you want to delete this activity?'),
      () => {
        deleteActivity(activityId).then(response => {
          if (response.success) {
            Toast.success(t('Activity was successfully deleted'));
            this.loadODataQuery();
          } else {
            Toast.warning(response.payload.message);
          }
        });
      }
    );
  }

  componentWillUnmount() {
    this.props.resetOData();
  }

  dataStateChange = e => {
    this.setState(
      {
        dataState: e.dataState
      },
      () => {
        this.loadODataQuery();
      }
    );
  };

  loadODataQuery = () => {
    this.props.queryOData(
      'ActivityQuery',
      this.state.dataState
    );
  };

  getCreatePathName() {
    return `${this.props.location.pathname.replace('/create', '')}/create`;
  }

  getBoolSwitch = value => {
    const { t } = this.props;
    return (
      <td>
        <div className="bool-column">
          <Switch disabled={true} checked={value} />
          {value ? t('Yes') : t('No')}
        </div>
      </td>
    );
  };

  getDefaultColumns() {
    const { t, disabled } = this.props;

    return {
      Name: {
        title: t('Name'),
        default: true,
        columnMenu: true,
        visible: true
      },
      Description: {
        title: t('Description'),
        default: true,
        columnMenu: true,
        visible: true
      },
      IsDefault: {
        title: t('Default activity'),
        width: '150px',
        default: false,
        visible: true,
        cell: ({ dataItem }) => this.getBoolSwitch(dataItem.IsDefault)
      },
      controls: {
        title: ' ',
        cell: this.controls,
        headerCell: () => <></>,
        default: true,
        visible: !disabled,
        resizable: false,
        width: '200px',
        sortable: false
      }
    };
  }

  controls = props => {
    const { t, disabled } = this.props;
    if (!disabled){
      return (
        <td className="activities-view--controls">
          <div className="controls">
            <GhostButton
              iconLeft={<Icon icon="pen" />}
              to={this.getActivityPathName(props.dataItem.ActivityId)}
            >
              {t('Edit')}
            </GhostButton>
            <GhostButton
              iconLeft={<Icon icon="delete" />}
              onClick={() => {
                this.onRemove(props.dataItem.ActivityId);
              }}
            >
              {t('Delete')}
            </GhostButton>
          </div>
        </td>
      );
    }

    return null;
  };

  getActivityPathName(activityId) {
    if (activityId) {
      return `${this.props.location.pathname.replace(
        '/activity',
        ''
      )}/activity/${activityId}`;
    }

    return `${this.props.location.pathname.replace('/activity', '')}/activity`;
  }

  render() {
    const { t, isLoading, match, disabled } = this.props;
    const { dataState } = this.state;

    return (
      <div className="activities-view">
        <ActivityDialog 
          location={location}
          history={history}
          match={match}
          isFirstActivity={!this.props.oData.data.length}
          reloadActivities={this.loadODataQuery}/>
        <H1>{t('Activity list')}</H1>
        <div>
          <div className="activities-view--header">
            <div className="activities-view--left">
              <RaisedButton
                iconLeft={<Icon icon="plus" />}
                to={this.getActivityPathName()}
                disabled={disabled}
              >
                {t('Add new Activity')}
              </RaisedButton>
            </div>
          </div>
          <Grid
            sortable={true}
            resizable={true}
            key={this.state.gridKey}
            ref={this.grid}
            pageable={{
              pageSizes: PAGE_SIZES
            }}
            {...dataState}
            {...this.props.oData}
            onDataStateChange={this.dataStateChange}
            onSortChange={event => {
              this.setState(
                {
                  dataState: {
                    ...dataState,
                    sort: event.sort
                  }
                },
                () => {
                  this.loadODataQuery();
                }
              );
            }}
          >
            {Columns({ 
              columns: this.state.columns,
              dataState,
              onSave: this.onUserSaveSettings,
              onReset: this.onUserResetSettings, })}
          </Grid>
          {isLoading && <GridLoader />}
        </div>
      </div>
    );
  }
}

Activities.propTypes = {
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  oData: PropTypes.object,
  queryOData: PropTypes.func,
  resetOData: PropTypes.func,
  loadODataQuery: PropTypes.func,
  deleteActivity: PropTypes.func,
  match: PropTypes.object,
  location: PropTypes.object,
  activityGrid: PropTypes.object,
  t: PropTypes.func
};

const oDataLoading = createLoadingSelector(['OData']);
const mapStateToProps = state => ({
  isLoading: oDataLoading(state),
  oData: state.odata
});

const mapDispatchToProps = {
  queryOData,
  resetOData,
  deleteActivity
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(Activities));
